import React, { useEffect, useMemo, useRef, useState } from 'react';
import RewardGlyph from './RewardGlyph';
import { describeReward, isSameReward, rarityColor } from '../utils/lootboxRewards';
import '../styles/lootbox.css';
import { useTranslation } from 'react-i18next';
import { entityText } from '../../../i18n/entityText';

/*
 * Chest opening = wheel of fortune.
 *
 * The prize is decided by the backend (POST /v1/lootbox/chests/{id}/open); the wheel
 * only plays it back. It starts spinning the moment the player taps Open (request in
 * flight), and as soon as the result arrives it plans a deceleration that stops the
 * pointer on a segment holding that prize (matched by contentId) so that the whole
 * spin takes ~SPIN_TOTAL_MS.
 */

const SPIN_TOTAL_MS  = 4000;  // from tap to stop
const MIN_DECEL_MS   = 2200;  // deceleration never shorter than this (slow network)
const ACCEL_MS       = 400;   // spin-up to full speed
const MAX_SPEED      = 900;   // deg / s at full speed
const MIN_SEGMENTS   = 8;     // prizes are repeated around the wheel up to this count
const REVEAL_DELAY_MS = 900;  // winner highlight before the reward popup

const WHEEL_R = 140;          // segment radius (viewBox units)
const HUB_R   = 40;

const toRad = (deg) => (deg * Math.PI) / 180;
// Point on a circle, angle measured clockwise from 12 o'clock
const polar = (r, deg) => [r * Math.sin(toRad(deg)), -r * Math.cos(toRad(deg))];
const mod = (a, n) => ((a % n) + n) % n;

function wedgePath(r, fromDeg, toDeg) {
  const [x1, y1] = polar(r, fromDeg);
  const [x2, y2] = polar(r, toDeg);
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M0 0 L${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

/** Repeats the drop table around the wheel: A B C D → A B C D A B C D. */
function buildSegments(contents) {
  if (!contents || contents.length === 0) {
    return Array.from({ length: MIN_SEGMENTS }, (_, i) => ({ key: `m${i}`, content: null }));
  }
  const n = contents.length;
  const count = n >= MIN_SEGMENTS ? n : n * Math.ceil(MIN_SEGMENTS / n);
  return Array.from({ length: count }, (_, i) => ({ key: `${contents[i % n].id}-${i}`, content: contents[i % n] }));
}

const haptic = (kind, style) => {
  const hf = window.Telegram?.WebApp?.HapticFeedback;
  if (!hf) return;
  if (kind === 'impact') hf.impactOccurred(style);
  else hf.notificationOccurred(style);
};

function Segment({ index, total, content, color, winner }) {
  const seg = 360 / total;
  const center = index * seg;
  const r = content ? describeReward(content) : null;
  const moduleImg = r?.type === 'CHARACTER_MODULE' ? r.imageUrl : null;
  const label = r ? (r.type === 'CHARACTER_MODULE' ? r.name : r.amount) : '?';

  return (
    <g className={winner ? 'lb-wheel-seg lb-wheel-seg--win' : 'lb-wheel-seg'}>
      <path
        d={wedgePath(WHEEL_R, center - seg / 2, center + seg / 2)}
        fill={index % 2 === 0 ? color : '#2a1450'}
        fillOpacity={index % 2 === 0 ? 0.85 : 1}
        stroke="#12072a"
        strokeWidth="2"
      />
      {/* Content is laid out along the segment's radius, facing outward */}
      <g transform={`rotate(${center})`}>
        {moduleImg
          ? <image href={moduleImg} x={-20} y={-WHEEL_R + 14} width={40} height={40} preserveAspectRatio="xMidYMid meet" />
          : <RewardGlyph type={r?.type} x={0} y={-WHEEL_R + 34} size={38} />}
        <text
          x="0"
          y={-WHEEL_R + 72}
          textAnchor="middle"
          className="lb-wheel-seg-text"
        >
          {label.length > 9 ? `${label.slice(0, 8)}…` : label}
        </text>
      </g>
    </g>
  );
}

export default function ChestOpeningAnimation({ chestType, contents, drop, failed, onDone, onFail }) {
  const { t } = useTranslation();
  const color = rarityColor(chestType?.rarity);
  const [winnerIndex, setWinnerIndex] = useState(null);
  // Result the wheel is playing back. If the drawn prize is missing from the
  // (possibly stale) drop table, it is written into one segment before stopping.
  const [override, setOverride] = useState(null);

  const baseSegments = useMemo(() => buildSegments(contents), [contents]);
  const segments = useMemo(() => {
    if (!override) return baseSegments;
    return baseSegments.map((s, i) => (i === override.index ? { ...s, content: override.content } : s));
  }, [baseSegments, override]);

  const rotorRef = useRef(null);
  const dropRef = useRef(drop);
  const failedRef = useRef(failed);
  const segmentsRef = useRef(baseSegments);
  const cbRef = useRef({ onDone, onFail });
  dropRef.current = drop;
  failedRef.current = failed;
  segmentsRef.current = baseSegments;
  cbRef.current = { onDone, onFail };

  useEffect(() => {
    let raf = 0;
    let doneTimer = 0;
    const startedAt = performance.now();
    let last = startedAt;
    let angle = 0;
    let plan = null;           // { from, distance, startAt, duration, index }
    let lastTickSeg = 0;
    let lastTickAt = 0;

    const planStop = (now) => {
      const segs = segmentsRef.current;
      const total = segs.length;
      const seg = 360 / total;
      const d = dropRef.current;

      let candidates = segs.map((s, i) => (isSameReward(s.content, d) ? i : -1)).filter(i => i >= 0);
      if (candidates.length === 0) {
        const index = Math.floor(Math.random() * total);
        setOverride({
          index,
          content: {
            id: d.contentId || 'drop',
            rewardType: d.rewardType,
            rewardValue: d.value,
            moduleImageUrl: d.characterModuleImageUrl,
          },
        });
        candidates = [index];
      }
      const index = candidates[Math.floor(Math.random() * candidates.length)];

      const elapsed = now - startedAt;
      const duration = Math.max(SPIN_TOTAL_MS - elapsed, MIN_DECEL_MS);
      // Cubic ease-out starts at 3·distance/duration — pick the distance that keeps
      // the current speed, then nudge it (±180°) so we land inside the target segment.
      const natural = (MAX_SPEED * duration) / 3000;
      const jitter = (Math.random() - 0.5) * seg * 0.6;
      const target = mod(-(index * seg) + jitter, 360);
      let delta = mod(target - (angle + natural), 360);
      if (delta > 180) delta -= 360;
      plan = { from: angle, distance: natural + delta, startAt: now, duration, index };
    };

    const tickHaptic = (now) => {
      const seg = 360 / segmentsRef.current.length;
      const current = Math.floor((angle + seg / 2) / seg);
      if (current !== lastTickSeg && now - lastTickAt > 70) {
        haptic('impact', 'light');
        lastTickAt = now;
      }
      lastTickSeg = current;
    };

    const frame = (now) => {
      if (failedRef.current) {
        cbRef.current.onFail?.();
        return;
      }
      const dt = (now - last) / 1000;
      last = now;
      const elapsed = now - startedAt;

      if (!plan) {
        const speed = MAX_SPEED * Math.min(1, elapsed / ACCEL_MS);
        angle += speed * dt;
        if (dropRef.current && elapsed >= ACCEL_MS) planStop(now);
      } else {
        const u = Math.min(1, (now - plan.startAt) / plan.duration);
        angle = plan.from + plan.distance * (1 - Math.pow(1 - u, 3));
        if (u >= 1) {
          rotorRef.current?.setAttribute('transform', `rotate(${angle})`);
          setWinnerIndex(plan.index);
          haptic('notification', 'success');
          doneTimer = setTimeout(() => cbRef.current.onDone?.(), REVEAL_DELAY_MS);
          return;
        }
      }

      rotorRef.current?.setAttribute('transform', `rotate(${angle})`);
      tickHaptic(now);
      raf = requestAnimationFrame(frame);
    };

    haptic('impact', 'medium');
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(doneTimer);
    };
  }, []);

  const bulbs = 24;

  return (
    <div className="lb-opening-overlay">
      <div className="lb-wheel-wrap" style={{ '--rarity-color': color }}>
        <div className="lb-wheel-title" style={{ color }}>{entityText(t, 'chest_type', chestType?.id, 'name', chestType?.name)}</div>

        <svg className="lb-wheel" viewBox="-165 -175 330 340" role="img" aria-label={t('lootbox.opening_aria', 'Opening chest')}>
          <defs>
            <radialGradient id="lbWheelHub" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#4a2a86" />
              <stop offset="100%" stopColor="#1b0b3a" />
            </radialGradient>
          </defs>

          {/* Outer rim with blinking bulbs */}
          <circle r={WHEEL_R + 16} fill="#1b0b3a" stroke={color} strokeWidth="4" />
          {Array.from({ length: bulbs }, (_, i) => {
            const [x, y] = polar(WHEEL_R + 8, (360 / bulbs) * i);
            return <circle key={i} cx={x} cy={y} r="3.2" className={`lb-wheel-bulb${i % 2 ? ' lb-wheel-bulb--alt' : ''}`} />;
          })}

          {/* Rotating part */}
          <g ref={rotorRef}>
            {segments.map((s, i) => (
              <Segment
                key={s.key}
                index={i}
                total={segments.length}
                content={s.content}
                color={color}
                winner={winnerIndex === i}
              />
            ))}
          </g>

          {/* Static hub with the chest */}
          <circle r={HUB_R} fill="url(#lbWheelHub)" stroke={color} strokeWidth="4" />
          {chestType?.imageUrl && (
            <image
              href={chestType.imageUrl}
              x={-HUB_R + 8}
              y={-HUB_R + 8}
              width={(HUB_R - 8) * 2}
              height={(HUB_R - 8) * 2}
              preserveAspectRatio="xMidYMid meet"
            />
          )}

          {/* Pointer at 12 o'clock */}
          <g className="lb-wheel-pointer">
            <path
              d={`M-15 ${-WHEEL_R - 30} L15 ${-WHEEL_R - 30} L0 ${-WHEEL_R + 4} Z`}
              fill="#fff"
              stroke={color}
              strokeWidth="3"
              strokeLinejoin="round"
            />
            <circle cx="0" cy={-WHEEL_R - 26} r="4" fill={color} />
          </g>
        </svg>

        <p className="lb-opening-label" style={{ color }}>
          {winnerIndex == null ? t('lootbox.opening', 'Opening…') : t('lootbox.you_got', 'You got!')}
        </p>
      </div>
    </div>
  );
}
